import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { api } from "../../lib/api";
import { useSEO } from "../../lib/seo";
import { MapPin, Briefcase, Clock, CurrencyDollar, ArrowRight, Funnel } from "@phosphor-icons/react";

const COUNTRIES = ["", "Canada", "United States", "Philippines", "India", "United Kingdom", "Pakistan", "Bangladesh", "Mexico", "Brazil", "Argentina", "Other"];
const LANGUAGES = ["", "English", "French", "Spanish", "Mandarin", "Hindi", "Tagalog", "Other"];

export default function VirtualJobsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: searchParams.get("category") || "",
    country: searchParams.get("country") || "",
    language: searchParams.get("language") || "",
    pricing_type: searchParams.get("pricing_type") || "",
    q: searchParams.get("q") || "",
  });
  const [categories, setCategories] = useState([]);

  useSEO({
    title: "Browse Virtual Staff Jobs",
    description: "Browse open virtual staff projects on CanHireSolution.com. Apply to remote work in admin, design, web development, marketing and more.",
    path: "/virtual/jobs",
  });

  useEffect(() => {
    api.get("/virtual/categories").then((r) => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/virtual/jobs", { params })
      .then((r) => setJobs(r.data))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
    setSearchParams(params);
  }, [filters, setSearchParams]);

  const upd = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="pt-24 pb-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <Link to="/virtual" className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 hover:text-slate-900">← Hire Virtual Staff</Link>
            <h1 className="font-display text-4xl mt-2">Open virtual projects</h1>
          </div>
          <button onClick={() => navigate("/virtual/jobs/new")} className="bg-brand text-white px-5 py-3 font-semibold hover:bg-[#122148]" data-testid="post-virtual-job-btn">
            Post a project
          </button>
        </div>

        {/* FILTERS */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-8 p-4 bg-slate-50 border border-slate-200">
          <input value={filters.q} onChange={(e) => upd("q", e.target.value)} placeholder="Search…" className="col-span-2 px-3 py-2 border border-slate-300 text-sm" data-testid="vfilter-q" />
          <select value={filters.category} onChange={(e) => upd("category", e.target.value)} className="px-3 py-2 border border-slate-300 text-sm" data-testid="vfilter-category">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={filters.country} onChange={(e) => upd("country", e.target.value)} className="px-3 py-2 border border-slate-300 text-sm" data-testid="vfilter-country">
            {COUNTRIES.map((c) => <option key={c || "any"} value={c}>{c || "Any country"}</option>)}
          </select>
          <select value={filters.language} onChange={(e) => upd("language", e.target.value)} className="px-3 py-2 border border-slate-300 text-sm" data-testid="vfilter-language">
            {LANGUAGES.map((l) => <option key={l || "any"} value={l}>{l || "Any language"}</option>)}
          </select>
          <select value={filters.pricing_type} onChange={(e) => upd("pricing_type", e.target.value)} className="px-3 py-2 border border-slate-300 text-sm" data-testid="vfilter-pricing">
            <option value="">Fixed & Hourly</option>
            <option value="fixed">Fixed price</option>
            <option value="hourly">Hourly</option>
          </select>
        </div>

        {/* RESULTS */}
        {loading ? (
          <div className="text-center py-16 text-slate-500">Loading…</div>
        ) : jobs.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-12 text-center">
            <Briefcase size={32} className="mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-600 mb-4">No matching projects right now.</p>
            <Link to="/virtual/jobs/new" className="inline-block bg-brand text-white px-5 py-3 font-semibold">Post the first one</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {jobs.map((j) => (
              <Link key={j.job_id} to={`/virtual/jobs/${j.job_id}`} className="border border-slate-200 p-5 hover:border-brand hover:-translate-y-1 transition-all" data-testid={`vjob-${j.job_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-accent-red">{j.service_type}</div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{j.pricing_type}</div>
                </div>
                <h3 className="font-display text-xl mb-2 leading-tight">{j.title}</h3>
                <p className="text-sm text-slate-600 line-clamp-2 mb-3">{j.description}</p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><CurrencyDollar size={12} weight="bold" /> ${j.budget_min}–${j.budget_max} CAD</span>
                  {j.deadline && <span className="flex items-center gap-1"><Clock size={12} weight="bold" /> Due {j.deadline}</span>}
                  {j.preferred_country && <span className="flex items-center gap-1"><MapPin size={12} weight="bold" /> {j.preferred_country}</span>}
                  <span>{j.application_count || 0} applicants</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
